import * as Sentry from '@sentry/browser';

import { isNormalAuthError } from '../errors';
import { reportExceptionIntegration } from './reportExceptionIntegration';
import type { SentryConfig } from './sentry';

/**
 * Initialize Sentry in this runtime.
 */
export function initializeSentry(config: SentryConfig) {
    Sentry.init({
        dsn: config.dsn,
        tunnel: config.tunnel,
        environment: config.projectId,
        release: config.release,
        tracePropagationTargets: [],
        replaysSessionSampleRate: config.recordReplays ? 1.0 : 0,
        replaysOnErrorSampleRate: config.recordReplays ? 1.0 : 0,
        ignoreErrors: [
            // Network errors such as going offline or being blocked by a proxy
            'Failed to fetch',
            'Network Error',
            'NetworkError',
            'CustomEvent',
            'Load failed',
            // Filter out annoying CefSharp issues
            // https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062/20
            'Non-Error promise rejection captured with value: Object Not Found Matching Id',
            /**
             * This error can be triggered by browsers during virtualization.
             * It means that ResizeObserver was not able to deliver all observations within a single animation frame.
             * It is benign (your site will not break).
             * Source - https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
             */
            /^ResizeObserver loop limit exceeded$/,
            // Scripts and stylesheets inserted in the <head> that never load, mostly
            // because an adblocker or a corporate proxy blocked them.
            /^Failed to load https?:\/\//,
        ],
        beforeSend: (event, hint) => {
            const error = hint.originalException || hint.syntheticException;

            // Filter normal Firebase auth errors.
            if (isNormalAuthError(error)) {
                return null;
            }

            // Grouping FirebaseErrors that are thrown when the "client is offline"
            // under the same sentry event fingerprint which can be thrown from
            // several places in the codebase with different stack traces.
            if (
                error instanceof Error &&
                error?.name === 'FirebaseError' &&
                error?.message?.match(/Failed to get document because the client is offline/i)
            ) {
                event.fingerprint = ['client-offline'];
            }

            // MagicBell often times out when fetching notifications, but it polls at
            // regular intervals, so it's hardly a problem. The error can surface as
            // a MagicBellError or as a raw TimeoutError from ky (MagicBell's HTTP client).
            if (
                error instanceof Error &&
                (error?.name === 'MagicBellError' || error?.name === 'TimeoutError') &&
                error?.message?.match(/Request timed out/i)
            ) {
                return null;
            }

            return event;
        },
        integrations: (defaultIntegrations) =>
            defaultIntegrations
                .map((integration) => {
                    if (integration.name === 'Breadcrumbs') {
                        // Console logging must be disabled since
                        // it's very slow to serialize some JS objects
                        return Sentry.breadcrumbsIntegration({
                            console: false,
                        });
                    }

                    return integration;
                })
                .concat([
                    reportExceptionIntegration(),
                    Sentry.replayIntegration({
                        maskAllText: false,
                        mask: [
                            // Hide all slate text nodes
                            '[data-offset-key]',

                            // Custom data that can be used to mask sensitive information
                            '[data-privacy-mask]',
                        ],
                    }),
                ]),
        // We don't track error when developing
        enabled: window.location.hostname !== '127.0.0.1' && !process.env.GITBOOK_NO_ANALYTICS,
    });
}

/**
 * Configure Sentry user / etc
 */
export const getSentryCurrentScope = Sentry.getCurrentScope;
export const getSentryGlobalScope = Sentry.getGlobalScope;

/**
 * Flush all events to Sentry.
 */
export async function flushToSentry(): Promise<void> {
    await Sentry.flush(2000);
}

/**
 * Start recording the current session.
 */
export function startSentryReplay() {
    const replay = Sentry.getReplay();
    if (replay) {
        replay.start();
    }
}
